<template>
  <ValidationObserver v-slot="{ invalid ,reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="computedTitle" @hidden="onHidden"
                   @hide="onHide" @show="onShow" modal-class="mobile-full-screen"
                   :show-confirmation="showConfirmation" :modal-loading="dynamicFieldsLoading"
                   :ok-title="$t('Submit')" :is-loading="loading" :content-in-form="false" scrollable>

      <template v-slot:modal-header>
        <div class="d-flex align-items-center">
          <h5 class="modal-title d-inline-block">
            {{ computedTitle }}
          </h5>
          <span class="ml-2 badge badge-secondary" v-if="model && model.is_draft">{{ $t('Draft') }}</span>
        </div>
        <button type="button" :disabled="loading" @click="hideModal" aria-label="Close" class="close">×
        </button>
      </template>

      <template v-if="modal.loading">
        <spinner show height="20" size="5"/>
      </template>

      <template v-else>
        <div class="row">
          <div class="col-xl-8 col-sm-12 border-right">
            <b-form ref="mainForm" @submit.prevent="onSubmit">
              <button type="submit" hidden></button>
              <b-alert variant="danger" :show="error && error.length > 0">
                {{ error }}
              </b-alert>

              <div class="row">
                <div class="col-sm-6 col-md-3 col-lg">
                  <receipt-select-input-widget :rules="isGroupedItem ? '' : 'required'"
                                               :disabled="shouldDisable || isGroupedItem"
                                               :expand="'item_locations'"
                                               :model="model" attribute="receipt_uuid" @select="onReceiptSelect"/>
                </div>
                <div class="col-sm-6 col-md-3 col-lg" v-if="!isSalesTaxEnabled">
                  <tax-status-select-input-widget attribute="tax_status_uuid" :model="model"
                                                  :rules="model.uuid ? 'required': ''" :hint="!model.uuid"/>
                </div>
                <div v-if="enableContinuousAuction && !isSalesTaxEnabled" class="col-sm-6 col-md-3 col-lg">
                  <input-widget :disabled="shouldDisable" :model="model" attribute="tax_rate" type="number"
                                :hint="!model.uuid"
                                :rules="model.uuid ? 'max_value:100|min_value:0|required' : 'max_value:100|min_value:0'"/>
                </div>
                <div class="col-sm-6 col-md-3 col-lg">
                  <input-widget :disabled="shouldDisable" :model="model" type="datepicker" attribute="receive_date"/>
                </div>
                <div class="col-sm-6 col-md-3 col-lg" v-if="isGroupedItem && !model.collected">
                  <item-location-select-input-widget :model="model" attribute="item_location_uuid"/>
                </div>
              </div>

              <div class="row">
                <div class="col-md-3">
                  <input-widget :model="model" attribute="quantity" type="number"
                                :disabled="isGroupedItem || shouldDisable" @input="onQuantityChange"/>
                </div>
                <div class="col-md-3">
                  <input-widget disabled :model="model" :label="$t('Available Quantity')"
                                attribute="available_quantity"/>
                </div>
                <div class="col-md-3 d-flex align-items-end mb-2" v-if="model.uuid">
                  <input-widget @change="onItemCollectedChange" :model="model" attribute="collected" type="checkbox"
                                is-switch size="lg" :disabled="shouldCollectedDisable || shouldDisable"/>
                </div>
              </div>

              <div class="row mb-3"
                   v-if="isGroupedItem && model.source_inventories">
                <div class="col-md-12">
                  <div class="card">
                    <div class="card-header d-flex align-items-center justify-content-between">
                      <div>
                        {{ $t('Inventory Items') }}
                      </div>
                      <b-button variant="link" class="p-0" @click="onAddNewSourceInventoryClick"
                                :disabled="shouldDisable">
                        <font-awesome-icon :icon="'plus-circle'"/>
                        {{ $t('Add New') }}
                      </b-button>
                    </div>
                    <div class="card-body pb-0">
                      <template v-if="model.source_inventories.length > 0">
                        <div class="row mb-2">
                          <div class="col-md-5">
                            <b>{{ $t("Item") }}</b>
                          </div>
                          <div class="col-md-5">
                            <b>{{ $t("Quantity") }}</b>
                          </div>
                        </div>
                        <div class="row"
                             v-for="(sourceItemModel, index) in model.source_inventories"
                             :key="`source-inventory-row-${index}`">
                          <div class="col-md-5">
                            <template v-if="!sourceItemModel.uuid">
                              <inventory-select-input-widget :model="sourceItemModel" :disabled="shouldDisable"
                                                             attribute="source_inventory_uuid"
                                                             :label="false" with-image/>
                            </template>
                            <template v-else>
                              <div class="pt-1">
                                {{ sourceItemModel.source_inventory.stock_no }} -
                                {{ sourceItemModel.source_inventory.short_description | striptags | truncate(50) }}
                              </div>
                            </template>
                          </div>
                          <div class="col-md-5">
                            <input-widget :model="sourceItemModel" attribute="quantity" type="number" :label="false"
                                          :disabled="shouldDisable"/>
                          </div>
                          <div class="col-md-1 text-center">
                            <b-tooltip :target="`remove-source-inventory-button-${index}`" :title="$t('Delete')"/>
                            <font-awesome-icon class="text-danger hover-cursor"
                                               :id="`remove-source-inventory-button-${index}`"
                                               :icon="'times'" @click="removeSourceInventoryByIndex(index)"
                                               style="font-size: 1.5em;margin-top: .2em"/>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <no-data-available height="5"/>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="enableContinuousAuction" class="card mb-3">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <div>
                    {{ $t('Lots') }}
                  </div>
                  <b-button variant="link" class="p-0" @click="addNewReservation" :disabled="shouldDisable">
                    <font-awesome-icon :icon="'plus-circle'"/>
                    {{ $t('Add New') }}
                  </b-button>
                </div>
                <div class="card-body pb-0" v-if="model.reservations && model.reservations.length > 0">
                  <div class="row" v-for="(reservation, index) in model.reservations"
                       :key="`cont-auction-reservation-row-${index}`"
                       :class="{'border-bottom mb-2': model.reservations && index < model.reservations.length - 1}">
                    <div class="col-md-11">
                      <div class="row">
                        <div class="col-md-3">
                          <input-widget :model="reservation.dates[0]" attribute="start_date" type="datepicker"
                                        :min="$options.filters.currentDate()" size="sm" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-3">
                          <input-widget :model="reservation.dates[0]" attribute="start_time" type="timepicker"
                                        size="sm" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-3">
                          <input-widget :model="reservation.dates[0]" attribute="end_date" type="datepicker"
                                        :min="$options.filters.currentDate()" size="sm" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-3">
                          <input-widget :model="reservation.dates[0]" attribute="end_time" type="timepicker"
                                        size="sm" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-3">
                          <input-widget :model="reservation" attribute="quantity"
                                        :rules="`required|max_value:${model.quantity}|min_value:1`"
                                        :disabled="reservation.isUnsoldOrWithdrawn() || shouldDisable"
                                        :placeholder="$t('Quantity')" type="number" size="sm"/>
                        </div>
                        <div class="col-md-3 mt-md-4">
                          <input-widget :model="reservation" attribute="collected" type="checkbox" is-switch
                                        size="md" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-3 mt-md-4">
                          {{ $t("Status") }}:
                          <span v-as-status="reservation.status">
                            {{ reservation.status | toUpperCaseFirst | removeUnderscores }}
                          </span>
                        </div>
                        <div class="col-md-3 mt-md-4" v-if="reservation.lot_no">
                          {{ $t("Lot") }}:
                          <span>
                            {{ reservation.lot_no }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-1 text-center" v-if="!shouldDisable">
                      <b-tooltip :target="`remove-reservation-button-${index}`" :title="$t('Delete')"/>
                      <font-awesome-icon class="text-danger hover-cursor mt-2"
                                         :id="`remove-reservation-button-${index}`"
                                         :icon="'times'" @click="removeReservationByIndex(index)"
                                         v-if="!reservation.isUnsoldOrWithdrawn()"
                                         style="font-size: 1.3em;"/>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="card mb-3">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <div>
                    {{ $t('Reservations') }}
                  </div>
                  <b-button variant="link" class="p-0" @click="addNewReservation" :disabled="shouldDisable">
                    <font-awesome-icon :icon="'plus-circle'"/>
                    {{ $t('Add New') }}
                  </b-button>
                </div>
                <div class="card-body pb-0" v-if="model.reservations && model.reservations.length > 0">
                  <div class="row mb-2">
                    <div class="col-md-3">
                      <b>{{ $t("Sale") }}</b>
                    </div>
                    <div class="col-md-2">
                      <b>{{ $t("Lot") }}</b>
                    </div>
                    <div class="col-md-2">
                      <b>{{ $t("Quantity") }}</b>
                    </div>
                    <div class="col-md-2 collected-checkbox">
                      <b>{{ $t("Collected") }}</b>
                    </div>
                    <div class="col-md-2">
                      <b>{{ $t("Status") }}</b>
                    </div>
                  </div>
                  <div :key="`non-cont-auction-reservation-row-${index}`"
                       class="row" v-for="(reservation, index) in model.reservations">

                    <template v-if="reservation.isUnsoldOrWithdrawnOrSold() || shouldDisable">
                      <div class="col-md-3">
                        {{ reservation.auction.sale_no }} -
                        {{ reservation.auction.title | masterLanguage | truncate(12) }}
                      </div>
                      <div class="col-md-2">
                        {{ reservation.lot_no }}
                      </div>
                      <div class="col-md-2">
                        {{ reservation.quantity }}
                      </div>
                    </template>
                    <template v-else>
                      <div class="col-md-3">
                        <sale-select-input-widget @select="onSaleSelect" value-field="uuid" :model="reservation"
                                                  attribute="auction_uuid" :label="false" rules="required"
                                                  :disabled="reservation.isUnsoldOrWithdrawn() || shouldDisable"
                                                  :options="{placeholder: $t('Sale')}"/>
                      </div>
                      <div class="col-md-2">
                        <input-widget :model="reservation" attribute="lot_no" :label="false"
                                      :rules="{regex: LOT_NO_REGEX}"
                                      :disabled="reservation.isUnsoldOrWithdrawn() || shouldDisable"
                                      :placeholder="$t('Lot')"/>
                      </div>
                      <div class="col-md-2">
                        <input-widget :model="reservation" attribute="quantity" :label="false"
                                      :rules="`required|max_value:${model.quantity}|min_value:1`"
                                      :disabled="reservation.isUnsoldOrWithdrawn() || shouldDisable"
                                      :placeholder="$t('Quantity')" type="number"/>
                      </div>
                    </template>


                    <div class="col-md-2 text-center collected-checkbox">
                      <input-widget :label="false" :model="reservation" attribute="collected" type="checkbox"
                                    is-switch
                                    size="lg"/>
                    </div>
                    <div class="col-md-2">
                      <router-link v-if="reservation && reservation.auction"
                                   :to="{name: 'sales.view', params: {id: reservation.auction.uuid}}">
                        <span class="badge mr-1" :class="statusMap[reservation.status]">
                          {{ reservation.status | toUpperCaseFirst | removeUnderscores }}
                        </span>
                      </router-link>
                      <span v-else class="badge mr-1" :class="statusMap[reservation.status]">
                        {{ reservation.status | toUpperCaseFirst | removeUnderscores }}
                      </span>
                    </div>
                    <div class="col-md-1 text-center" v-if="!shouldDisable">
                      <b-tooltip :target="`remove-reservation-button-${index}`" :title="$t('Delete')"/>
                      <font-awesome-icon class="text-danger hover-cursor" :id="`remove-reservation-button-${index}`"
                                         :icon="'times'" @click="removeReservationByIndex(index)"
                                         v-if="!reservation.isUnsoldOrWithdrawn()"
                                         style="font-size: 1.5em;margin-top: .2em"/>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card mb-3" v-if="model.financial_items && model.financial_items.length">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <div>
                    {{ $t('Manual Reservations') }}
                  </div>
                </div>
                <div class="card-body pb-0">
                  <div class="row" v-for="(financialItem) in model.financial_items" :key="financialItem.uuid">
                    <div class="col-md-4">
                      <input-widget :model="financialItem" attribute="invoice_no" :label="false"
                                    :placeholder="$t('Invoice')" type="text" disabled/>
                    </div>
                    <div class="col-md-4">
                      <input-widget :model="financialItem" attribute="quantity" :label="false"
                                    :placeholder="$t('Quantity')" type="text" disabled/>
                    </div>
                    <div class="col-md-4 d-flex align-items-center">
                      <input-widget :model="financialItem" attribute="collected" type="checkbox" is-switch size="lg"
                      />
                    </div>

                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12 col-lg-4" v-if="!model.collected && !isGroupedItem">
                  <item-location-select-input-widget :model="model" attribute="item_location_uuid"/>
                </div>
                <div class="col-md-12 col-lg-4">
                  <department-select-input-widget @select="onDepartmentSelect" :disabled="shouldDisable" :model="model"
                                                  @unselect="onDepartmentUnselect"
                                                  attribute="department_uuid"/>
                </div>
                <div class="col-md-12 col-lg-4">
                  <category-select-input-widget @select="onDynamicFieldsCategorySelect" get-dynamic-fields-template
                                                :model="model" :disabled="shouldDisable"
                                                :department="model.department" attribute="category_uuid"/>
                </div>
              </div>

              <div class="row" v-if="!isGroupedItem">
                <div class="col-md-4">
                  <user-select-input-widget :model="model" attribute="specialists_uuids" multiple
                                            :items="model.specialists"
                                            :disabled="shouldDisable || !model.department_uuid"
                                            :filter-extra-data="specialistExtraFilters"/>
                </div>
                <div class="col-md-4">
                  <cataloguing-lifecycle-select-input-widget attribute="cataloguing_lifecycle" :model="model"
                                                             :disabled="shouldDisable"
                                                             :hint="lifecycleLastUpdatedHint('cataloguing_lifecycle')"
                                                             :options="{placeholder: $t('Cataloguing Lifecycle')}"/>
                </div>
                <div class="col-md-4">
                  <photography-lifecycle-select-input-widget attribute="photography_lifecycle" :model="model"
                                                             :disabled="shouldDisable"
                                                             :hint="lifecycleLastUpdatedHint('photography_lifecycle')"
                                                             :options="{placeholder: $t('Photography Lifecycle')}"/>
                </div>
              </div>
              <div class="row" v-else>
                <div class="col-md-6">
                  <cataloguing-lifecycle-select-input-widget attribute="cataloguing_lifecycle" :model="model"
                                                             :disabled="shouldDisable"
                                                             :hint="lifecycleLastUpdatedHint('cataloguing_lifecycle')"
                                                             :options="{placeholder: $t('Cataloguing Lifecycle')}"/>
                </div>
                <div class="col-md-6">
                  <photography-lifecycle-select-input-widget attribute="photography_lifecycle" :model="model"
                                                             :disabled="shouldDisable"
                                                             :hint="lifecycleLastUpdatedHint('photography_lifecycle')"
                                                             :options="{placeholder: $t('Photography Lifecycle')}"/>
                </div>
              </div>
              <div class="card mb-2" v-if="isCarAuctionHouse && computedSpecialDynamicFields.length ">
                <div class="card-body">
                  <translatable-input-widget v-if="!dynamicFieldsLoading" :disabled="shouldDisable" :hint="false"
                                             :model="model" :fields="computedSpecialDynamicFields" :errors="errors"
                                             input-class="col-md-4"
                                             @onNewValuedAddedToDropdown="onNewValuedAddedToDropdown"/>
                  <div class="text-right">
                    <b-button variant="primary" @click="onCheckCodeClick()" :disabled="checking">
                      <span v-if="checking"
                            class="spinner-border spinner-border-sm"
                            role="status"
                            style="width: 1.3em;height: 1.3em;margin-right: 1em"
                            aria-hidden="true"></span>
                      {{ $t('Check') }}
                    </b-button>
                  </div>
                </div>
              </div>

              <translatable-input-widget v-if="!dynamicFieldsLoading" :disabled="shouldDisable" :hint="false"
                                         :model="model" :fields="computedDynamicFields" :errors="errors"
                                         ref="mainTranslatableWidget" skip-disabling-by-type="checkbox"
                                         @onInputChange="onDynamicFieldTranslationInputChange"
                                         @onNewValuedAddedToDropdown="onNewValuedAddedToDropdown"/>

              <div class="row mb-3" v-if="translationProvider">
                <div class="col-md-12">
                  <b-button variant="primary" @click="requestTranslations" :disabled="loading">
                    <font-awesome-icon icon="language"/>
                    {{ $t('Get Translations') }}
                  </b-button>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-12">
                  <div class="card collection-info-card">
                    <div class="card-header d-flex align-items-center">
                      <input-widget :model="model" :disabled="shouldDisable" attribute="collection_info_enabled"
                                    type="checkbox"
                                    :is-switch="true"
                                    size="lg" @change="setCollectionInformation"/>
                    </div>
                    <div class="card-body pb-0" v-if="model.collection_info_enabled">
                      <div class="row">
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_address_1"
                                        :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_address_2" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_state" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_municipality"
                                        :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_city" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <post-code-input-widget
                            :model="model" attribute="collection_info_zip_code" :src="INVENTORY_FORM"
                            :disabled="shouldDisable">
                          </post-code-input-widget>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_phone" :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_email"
                                        :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_notes"
                                        :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_first_name"
                                        :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget :model="model" attribute="collection_info_last_name"
                                        :disabled="shouldDisable"/>
                        </div>
                        <div class="col-md-6">
                          <input-widget
                            :model="model" attribute="longitude" type="number" min="-180" step="any"
                            vid="item-longitude"
                            :disabled="shouldDisable">
                          </input-widget>
                        </div>
                        <div :class="isGoogleMapsEnabled ? 'col-md-5' : 'col-md-6'">
                          <input-widget
                            :model="model" attribute="latitude" type="number" min="-85" step="any" vid="item-latitude"
                            :disabled="shouldDisable">
                          </input-widget>
                        </div>
                        <div class="col-md-1 d-flex align-items-center globe-container" v-if="isGoogleMapsEnabled">
                          <b-tooltip target="globe-btn" :title="$t('Google Maps')"/>
                          <b-button variant="primary" id="globe-btn" :disabled="disableGlobe"
                                    @click="onShowGoogleMapsClick">
                            <font-awesome-icon icon="globe-europe"/>
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <user-select-input-widget :model="model" attribute="account_managers_uuid"/>
                </div>
                <div class="col-md-12 col-lg-4">
                  <user-select-input-widget :model="model" attribute="rep_user_uuid"/>
                </div>
                <div class="col-md-12 col-lg-4">
                  <user-select-input-widget :model="model" attribute="expert_user_uuid"/>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-6 col-xl-3">
                  <currency-input-widget :disabled="shouldDisable" :currency="saleCurrency" :model="model"
                                         attribute="low"
                                         :append-question="getAmountFieldQuestionText('low')"/>
                </div>
                <div class="col-lg-6 col-xl-3">
                  <currency-input-widget :disabled="shouldDisable" :currency="saleCurrency" :model="model"
                                         attribute="high"
                                         :append-question="getAmountFieldQuestionText('high')"/>
                </div>
                <div class="col-lg-6 col-xl-3">
                  <currency-input-widget :disabled="shouldDisable" :currency="saleCurrency" :model="model"
                                         attribute="start_price"
                                         :rules="startPriceRules"
                                         :append-question="getAmountFieldQuestionText('start_price')"/>
                </div>
                <div class="col-lg-6 col-xl-3">
                  <currency-input-widget :disabled="shouldDisable" :currency="saleCurrency" :model="model"
                                         attribute="reserve" @input="onReserveChange"
                                         :append-question="getAmountFieldQuestionText('reserve')"/>
                </div>
                <div class="col-lg-6 col-xl-3" v-if="showPricePerUnitField">
                  <currency-input-widget :disabled="shouldDisable" :currency="saleCurrency" :model="model"
                                         attribute="reserve_per_unit"/>
                </div>
                <div class="col-lg-6 col-xl-3">
                  <currency-input-widget :disabled="shouldDisable" :currency="saleCurrency" :model="model"
                                         attribute="buy_now_price"
                                         :append-question="getAmountFieldQuestionText('buy_now_price')"/>
                </div>
                <div class="col-lg-6 col-xl-3">
                  <reserve-type-select-input-widget :model="model" attribute="reserve_type"/>
                </div>
                <div class="col-md-4 net-reserve d-flex align-items-center">
                  <input-widget :disabled="shouldDisable" :model="model" attribute="net_reserve" type="checkbox"/>
                </div>
              </div>


            </b-form>
          </div>
          <div class="col-xl-4 col-sm-12">
            <images-view :hide-save-btn="true" :source="modal.source" :inventory-uuid="model.uuid" preserve-deleted-uuids
                         :run-polling-on-save="false" ref="imageViewer" enable-image-edit :disabled="shouldDisable"
                         :department="model.department || null"/>
          </div>
        </div>
        <google-maps-modal @on-coordinate-select="onCoordinateSelect"/>
      </template>

      <template v-slot:modal-footer>
        <template v-if="enableInventoryDraft">
          <cancel-button @click="hideModal" :disabled="loading">
            {{ $t('Cancel') }}
          </cancel-button>
          <submit-button @click="onDraftSubmit" :disabled="loading" :show-loader="loading">
            {{ $t('Save as Draft') }}
          </submit-button>
          <submit-button @click="onSubmit()" :disabled="loading" :show-loader="loading">
            {{ model && model.published_at_least_once ? $t('Save & Publish') : $t('Save') }}
          </submit-button>
        </template>
        <template v-else>
          <cancel-button @click="hideModal" :disabled="loading">
            {{ $t('Cancel') }}
          </cancel-button>
          <submit-button @click="onSubmitWithoutClose()" :disabled="loading" :show-loader="loading">
            {{ $t('Save') }}
          </submit-button>
          <submit-button @click="onSubmit()" :disabled="loading" :show-loader="loading">
            {{ $t('Save & Close') }}
          </submit-button>
        </template>
      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import InputWidget from "../../core/components/input-widget/InputWidget";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import InventoryModel from "./Inventory.model";
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import DepartmentSelectInputWidget from "../../core/widgets/DepartmentSelectInputWidget";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import {
    INVENTORY_FORM,
    SOURCE_INVENTORY_DASHBOARD,
    SOURCE_INVENTORY_VIEW,
    WS_TRANSLATION_REQUEST
  } from "../constants";
  import TranslatableInputWidget from "../../core/widgets/TranslatableInputWidget";
  import ItemLocationSelectInputWidget from "../../core/widgets/ItemLocationSelectInputWidget";
  import SaleSelectInputWidget from "../../core/widgets/SaleSelectInputWidget";
  import ImagesView from "./view/images/ImagesView";
  import CategorySelectInputWidget from "../../core/widgets/CategorySelectInputWidget";
  import ReservationModel from "@/modules/inventory/Reservation.model";
  import {LOT_NO_REGEX} from '@/plugins/vee-validate';
  import FinancialItemModel from "@/modules/invoices/models/FinancialItem.model";
  import GoogleMapsModal from "./GoogleMapsModal";
  import {loadGmapApi} from "vue2-google-maps";
  import ReceiptSelectInputWidget from "../../core/widgets/ReceiptSelectInputWidget";
  import PostCodeInputWidget from "../../core/widgets/PostCodeInputWidget";
  import Spinner from "@/core/components/common/Spinner";
  import InventorySelectInputWidget from "@/core/widgets/InventorySelectInputWidget";
  import SourceInventoryModel from "@/modules/inventory/group-items/SourceInventory.model";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import InventoryService from "@/modules/inventory/InventoryService";
  import SalesService from "@/modules/sales/SalesService";
  import CancelButton from "@/core/components/common/CancelButton";
  import SubmitButton from "@/core/components/common/SubmitButton";
  import debounce from "lodash/debounce";
  import LotsService from "../lots/LotsService";
  import ReserveTypeSelectInputWidget from "@/core/widgets/ReserveTypeSelectInputWidget";
  import helperService from "@/core/services/helper.service";
  import TaxStatusSelectInputWidget from "@/core/widgets/TaxStatusSelectInputWidget";
  import uuidV4 from 'uuid';
  import CataloguingLifecycleSelectInputWidget from "@/core/widgets/CataloguingLifecycleSelectInputWidget.vue";
  import PhotographyLifecycleSelectInputWidget from "@/core/widgets/PhotographyLifecycleSelectInputWidget.vue";


  const {mapState, mapActions} = createNamespacedHelpers('inventory');
  const {
    mapState: mapStateGlobal,
    mapActions: mapGlobalActions,
    mapGetters: mapGettersGlobal
  } = createNamespacedHelpers('global');
  const {mapActions: departmentMapActions} = createNamespacedHelpers('setup/department');

  export default {
    name: "InventoryModal",
    components: {
      PhotographyLifecycleSelectInputWidget,
      CataloguingLifecycleSelectInputWidget,
      TaxStatusSelectInputWidget,
      ReserveTypeSelectInputWidget,
      SubmitButton,
      CancelButton,
      NoDataAvailable,
      InventorySelectInputWidget,
      Spinner,
      PostCodeInputWidget,
      ReceiptSelectInputWidget,
      GoogleMapsModal,
      CategorySelectInputWidget,
      ImagesView,
      SaleSelectInputWidget,
      ItemLocationSelectInputWidget,
      TranslatableInputWidget,
      CurrencyInputWidget,
      DepartmentSelectInputWidget,
      UserSelectInputWidget,
      InputWidget,
      ArtisioModal,
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        requiresTranslation: false,
        model: new InventoryModel(),
        saleCurrency: null,
        initialTaxStatus: null,
        initialTaxRate: null,
        initialReceipt: null,
        LOT_NO_REGEX: LOT_NO_REGEX,
        specialFields: ['vrm_number', 'vin_number', 'mileage', 'spec', 'mot', 'black_book',],
        checking: false,
        dynamicFieldsLoading: false,
        INVENTORY_FORM: INVENTORY_FORM,
        shouldRefreshAllDepartmentsAfterSave: false,
        itemsInitiallyCollectedData: {
          financial_item_uuids: new Set(),
          reservation_uuids: new Set()
        },
        itemInitiallyCollected: null,
        locationBeforeRelease: null,
        statusMap: {
          lotted: "bg-primary text-white",
          reserved: "bg-primary text-white",
          sold: "bg-success text-white",
          withdrawn: "bg-warning text-white",
          unsold: "bg-danger text-white",
        }
      }
    },
    computed: {
      ...mapGettersGlobal(['showPricePerUnitField']),
      ...mapStateGlobal([
        'currentBranch',
        'enableInventoryQuantity',
        'enableContinuousAuction',
        'continuousAuctionLotFormDefaultDatesConfiguration',
        'currentUser',
        'collectionInfoEnabled',
        'generalRulesAndRestrictions',
        'isCarAuctionHouse',
        'generalRulesAndRestrictions',
        'isDynamicFieldsTemplateFromCategory',
        'googleMapToken',
        'isGoogleApiSetUp',
        'isSalesTaxEnabled',
        'defaultItemLocation',
        'autoPopulateAuctionInReservation',
        'enableInventoryDraft',
        'disableItemEditOnSold',
        'locales',
        'inventoryLotAutoSelectEnable',
        'translationProvider',
        'itemLifecycleOptions',
        'cataloguingLifecycleOptions',
        'photographyLifecycleOptions',
      ]),
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object,
        error: state => state.modal.error,
        images: state => state.view.images,
      }),
      isGroupedItem() {
        return this.model && this.model.is_grouped_item
      },
      isGoogleMapsEnabled() {
        return !!this.googleMapToken;
      },
      shouldCollectedDisable() {
        return this.enableInventoryQuantity
          && (this.model.reservations && this.model.reservations.length > 0
            || this.model.financial_items && this.model.financial_items.length > 0)
      },
      computedTitle() {
        if (this.model.receipt && this.model.uuid) {
          return this.$i18n.t('Edit Inventory Item {stock_no}', {stock_no: `${this.model.receipt.receipt_no}/${this.model.item_no}`});
        } else {
          if (this.isGroupedItem) {
            return this.$i18n.t("New Grouped Inventory Item");
          }
          return this.$i18n.t("New Inventory Item");
        }
      },
      computedDynamicFields() {
        if (!this.isCarAuctionHouse) {
          return this.model.sorted_dynamic_fields_template
        }
        return this.model.sorted_dynamic_fields_template.filter(item => {
          return !this.specialFields.includes(item.key)
        })
      },
      computedSpecialDynamicFields() {
        let returnArr = [];
        let keyToFieldMap = {}
        let availableKeys = []

        this.model.sorted_dynamic_fields_template.forEach((item) => {
          keyToFieldMap[item.key] = item;
          availableKeys.push(item.key);
        })

        this.specialFields.forEach((i) => {
          if (availableKeys.includes(i)) {
            returnArr.push(keyToFieldMap[i])
          }
        });

        return returnArr;
      },
      disableGlobe() {
        let disabled = this.isGlobeDisabled('longitude')

        if (!disabled) {
          disabled = this.isGlobeDisabled('latitude')
        }

        return this.shouldDisable || disabled;
      },
      showReservationTiers() {
        return true
      },
      startPriceRules() {
        if (this.generalRulesAndRestrictions.start_price_lower_than_reserve && this.model.reserve) {
          return `max_value:${this.model.reserve - 1}|min_value:0`;
        }
        return `min_value:0`;
      },
      collectedReservationsCountWithQuantity() {
        if (!this.model || !this.model.reservations || this.model.reservations.length === 0) {
          return 0
        }
        let tmp = 0;
        this.model.reservations.forEach(r => {
          if (r.collected) {
            tmp = tmp + parseInt(r.quantity)
          }
        })
        return tmp
      },
      collectedFinancialItemsCountWithQuantity() {
        if (!this.model || !this.model.financial_items || this.model.financial_items.length === 0) {
          return 0
        }
        let tmp = 0;
        this.model.financial_items.forEach(fi => {
          if (fi.collected) {
            tmp = tmp + parseInt(fi.quantity)
          }
        })
        return tmp
      },
      itemsCount() {
        return (this.model.reservations ? this.model.reservations.length : 0) + (this.model.financial_items ? this.model.financial_items.length : 0)
      },
      areAllItemsCollected() {
        return (this.collectedReservationsCountWithQuantity + this.collectedFinancialItemsCountWithQuantity) === parseInt(this.model.quantity)
      },
      shouldDisable() {
        if (!this.disableItemEditOnSold) {
          return false
        }

        if (this.model && this.model.reservations && this.model.reservations.filter(r => r.status === 'sold' && r.auction && r.auction.status === 'completed').length > 0) {
          return true
        }

        if (this.model.financial_items && this.model.financial_items.filter(fi => fi.invoice && fi.invoice.status === 'paid').length > 0) {
          return true
        }

        return false
      },
      specialistExtraFilters() {
        return {specialist_department_uuid: this.model && this.model.department_uuid ? this.model.department_uuid : null};
      }
    },
    watch: {
      async editObject() {
        if (!this.editObject) {
          return;
        }
        if (this.translationProvider) {
          this.requiresTranslation = false;
        }
        if (this.editObject.tax_status_uuid) {
          this.initialTaxStatus = this.editObject.tax_status_uuid;
        }

        if (this.editObject.tax_rate) {
          this.initialTaxRate = this.editObject.tax_rate;
        }

        if (this.editObject.receipt_uuid) {
          this.initialReceipt = this.editObject.receipt_uuid;
        }

        if (this.editObject.auction && this.editObject.auction.currency) {
          this.saleCurrency = this.editObject.auction.currency.symbol;
        }

        if (this.editObject.uuid) {
          this.model = new InventoryModel(this.editObject);
          this.itemInitiallyCollected = this.model.collected
          this.itemsInitiallyCollectedData = this.getCollectedItemsData()
          this.locationBeforeRelease = this.model.pre_release_item_location_uuid || null
          return
        }

        if (this.editObject.isDuplicate) {
          this.model = new InventoryModel(this.editObject);
          if (this.model.department) {
            this.model.initDynamicFields(this.model, this.model.category.dynamic_fields_template);
          }
          return
        }

        if (this.editObject.is_grouped_item) {
          this.model = new InventoryModel({
            ...this.editObject,
            ...this.getBlankCreateLifecycleDefaults(this.editObject)
          });
          return
        }

        let itemLocationUuid = this.defaultItemLocation ? this.defaultItemLocation.uuid : null
        itemLocationUuid = this.editObject.receipt && this.editObject.receipt.item_locations && this.editObject.receipt.item_locations.length ? this.editObject.receipt.item_locations[0].item_location_uuid : itemLocationUuid

        this.model = new InventoryModel({
          ...this.editObject,
          department_uuid: this.editObject.receipt && this.editObject.receipt.department ? this.editObject.receipt.department.uuid : null,
          item_location_uuid: itemLocationUuid,
          rep_user_uuid: this.currentUser.uuid,
          rep_user: this.currentUser,
          collection_info_enabled: this.collectionInfoEnabled,
          ...this.getBlankCreateLifecycleDefaults(this.editObject)
        });

        if (this.editObject.receipt && this.editObject.receipt.department) {
          this.onDepartmentSelect(this.editObject.receipt.department)
        }

        this.itemInitiallyCollected = this.model.collected
        this.itemsInitiallyCollectedData = this.getCollectedItemsData()
      },
      areAllItemsCollected(val) {
        if (val && !this.model.collected) {
          if (!this.locationBeforeRelease) {
            this.locationBeforeRelease = this.model.item_location_uuid;
          }
        } else if (!val && this.model.collected) {
          const restoreUuid = this.model.pre_release_item_location_uuid || this.locationBeforeRelease;
          if (restoreUuid) {
            this.model.item_location_uuid = restoreUuid;
          }
        }
        this.model.collected = val;
      },
      'model.quantity': {
        handler() {
          if (this.itemsCount !== 0) {
            this.model.collected = this.areAllItemsCollected;
          }
          this.calcAvailableQuantity();
        },
        'model.reservations': {
          deep: true,
          handler() {
            this.calcAvailableQuantity();
          }
        },
      },
      'model.reservations': {
        handler() {
          this.calcAvailableQuantity();
        }
      },
      'model'() {
        if (this.model && this.model.financial_items) {
          this.init();
        }
      }
    },
    methods: {
      ...departmentMapActions(['getDepartments']),
      ...mapActions([
        'hideModal',
        'createInventory',
        'updateInventory',
        'updateViewInventory',
        'getInventory',
        'showGoogleMapsModal',
        'getLongAndLat',
        'createGroupedInventory',
        'updateGroupedInventory',
        'getModalObject',
        'updateDeletedImages',
      ]),
      ...mapGlobalActions([
        'changeGoogleApiSetUp',
        'configureContinuousAuctionLotFormDefaultDatesConfiguration'
      ]),
      onNewValuedAddedToDropdown() {
        this.shouldRefreshAllDepartmentsAfterSave = true
      },
      lifecycleLastUpdatedHint(field) {
        const entry = (this.model.lifecycle_last_updated || {})[field];
        // Guarding on uuid keeps the line off the Create form and off a duplicate, which is
        // built from the source item's payload with only the uuid stripped.
        if (!this.model.uuid || !entry || !entry.created_by) {
          return false;
        }

        return this.$t(entry.value ? 'Last updated by {name} at {date}' : 'Cleared by {name} at {date}', {
          name: entry.created_by.display_name,
          date: this.$options.filters.toDateTime(entry.created_at),
        });
      },
      getBlankCreateLifecycleDefaults(existing = {}) {
        return {
          cataloguing_lifecycle: existing.cataloguing_lifecycle || helperService.getDefaultLifecycleOptionKey(
            this.cataloguingLifecycleOptions, 'cataloguing_lifecycle_options'
          ),
          photography_lifecycle: existing.photography_lifecycle || helperService.getDefaultLifecycleOptionKey(
            this.photographyLifecycleOptions, 'photography_lifecycle_options'
          ),
        };
      },
      onItemCollectedChange(val) {
        if (val) {
          // Remember location before Released is assigned on save
          if (!this.locationBeforeRelease) {
            this.locationBeforeRelease = this.model.item_location_uuid;
          }
        } else {
          // Restore pre-release / remembered location when collected is unchecked
          const restoreUuid = this.model.pre_release_item_location_uuid || this.locationBeforeRelease;
          if (restoreUuid) {
            this.model.item_location_uuid = restoreUuid;
          }
        }
        this.model.collected = val
        for (let reservation of this.model.reservations) {
          reservation.collected = this.model.collected;
        }
        for (let financial_item of this.model.financial_items) {
          financial_item.collected = this.model.collected;
        }
      },
      onShow() {
        this.addInitialReservation()
        this.dynamicFieldsLoading = true;
        this.showConfirmation = true;
        this.setCollectionInformation(this.collectionInfoEnabled);

        // if (this.model.category && this.model.category.dynamic_fields_template) {
        //   this.model.initDynamicFields(this.model, this.model.category.dynamic_fields_template);
        // } else if (this.model.department && this.model.department.dynamic_fields_template) {
        //   this.model.initDynamicFields(this.model, this.model.department.dynamic_fields_template);
        // }

        this.$nextTick(() => {
          this.dynamicFieldsLoading = false;
        })
      },
      onHide() {
        this.updateDeletedImages([])
        if (this.$refs.imageViewer) {
          this.$refs.imageViewer.clearPollingInterval();
        }
      },
      onHidden() {
        this.model = new InventoryModel();
        this.itemsInitiallyCollectedData = {
          financial_item_uuids: new Set(),
          reservation_uuids: new Set()
        };
        this.hideModal();
      },
      getCollectedItemsData() {
        const itemsData = {
          financial_item_uuids: new Set(),
          reservation_uuids: new Set()
        }
        const hasReservations = (this.model && this.model.reservations && !(this.model.reservations.length === 0))
        if (hasReservations) {
          this.model.reservations.forEach(r => {
            if (r.collected) {
              itemsData.reservation_uuids.add(r.uuid)
            }
          })
        }
        const hasFinancialItems = (this.model && this.model.financial_items && !(this.model.financial_items.length === 0))
        if (hasFinancialItems) {
          this.model.financial_items.forEach(fi => {
            if (fi.collected) {
              itemsData.financial_item_uuids.add(fi.uuid)
            }
          })
        }
        return itemsData
      },

      onSaleSelect(sale) {
        this.saleCurrency = sale.currency.symbol;
        this.getNextLotNumbersForReservations()
      },
      onDepartmentSelect(department) {
        // this.dynamicFieldsLoading = true;
        this.model.department = department;
        this.model.specialists_uuids = [];
        this.model.specialists = [];
        this.model.category_uuid = null;
        // this.model.initDynamicFields(this.model, this.model.department.dynamic_fields_template)
        // this.model = new InventoryModel(this.model, false)
        // this.$nextTick(() => {
        //   this.dynamicFieldsLoading = false;
        // })
      },
      onDepartmentUnselect() {
        this.model.specialists_uuids = [];
        this.model.specialists = [];
      },
      onDynamicFieldsCategorySelect(category) {
        this.dynamicFieldsLoading = true;
        this.model.resetErrors('category_uuid')
        this.model.category = category;
        this.model.category_uuid = category.uuid;
        this.model.initDynamicFields(this.model, this.model.category.dynamic_fields_template)
        this.model = new InventoryModel(this.model, false)
        this.$nextTick(() => {
          this.dynamicFieldsLoading = false;
        })
        this.getNextLotNumbersForReservations()
      },
      async getNextLotNumbersForReservations() {
        if (!this.inventoryLotAutoSelectEnable) {
          return;
        }

        if (this.model.category_uuid && this.model.reservations.length > 0 && this.allReservationsHaveSale()) {
          let toSendReservations = this.model.reservations.map((res, index) => ({
            auction_uuid: res.auction_uuid,
            lot_no: res.lot_no,
            status: res.status,
            index
          })).filter(r => r.status === 'reserved')

          if (toSendReservations.length === 0) {
            return;
          }

          const payload = {
            reservations: toSendReservations,
            category_uuid: this.model.category_uuid
          };
          const {body: reservations, success} = await LotsService.getNextLotNumbers(payload)
          if (success) {
            for (let r of reservations) {
              let index = r.index
              let res = this.model.reservations[index];
              res.errors = {};
              if (r.error) {
                res.errors.lot_no = [r.error]
              }
              res.lot_no = r.lot_no
            }
          } else {
            console.error('Unable to fetch next lot not')
          }
        }
      },
      allReservationsHaveSale() {
        return this.model.reservations.every(res => !!res.auction_uuid)
      },
      copyDynamicFieldsToOtherLanguagesIfNecessary() {
        if (!this.locales || !this.locales.available_locales || Object.keys(this.locales.available_locales).length < 2
          || !this.computedDynamicFields || this.computedDynamicFields.length === 0) {
          return;
        }

        let requiredDynamicFieldKeys = this.computedDynamicFields.filter(f => f.rules.required).map(f => f.key)

        this.model.assignToDynamicFields()

        let localeWithAllRequiredFields = null;

        // searching for the locale which has all required dynamic fields filled
        for (let locale in this.model.dynamic_fields) {
          let currentValues = this.model.dynamic_fields[locale];
          let hasAllRequiredDynamicFieldsFilled = true;
          for (let key of requiredDynamicFieldKeys) {
            if (!currentValues[key]) {
              hasAllRequiredDynamicFieldsFilled = false;
              break;
            }
          }
          if (hasAllRequiredDynamicFieldsFilled) {
            localeWithAllRequiredFields = locale;
            break;
          }
        }

        // if we did not find any locale with all fields filled, do nothing, which will automatically throw validation error
        if (!localeWithAllRequiredFields) {
          return;
        }

        // checking whether all the other locale fields are empty
        let hasAtLeastOneLocaleWithAnyValueFilled = false;
        for (let locale in this.model.dynamic_fields) {
          if (locale === localeWithAllRequiredFields) {
            continue;
          }

          let currentValues = this.model.dynamic_fields[locale];
          for (let key of requiredDynamicFieldKeys) {
            if (currentValues[key]) {
              hasAtLeastOneLocaleWithAnyValueFilled = true;
              break;
            }
          }

          if (hasAtLeastOneLocaleWithAnyValueFilled) {
            break;
          }
        }

        // if we find at least one other locale which has at least one value field filled, do nothing, which will automatically throw validation error
        if (hasAtLeastOneLocaleWithAnyValueFilled) {
          return;
        }

        // if the code reached this stage, it means that there is 1 locale fields filled entirely and others are all empty, we should just copy all the fields to other locales
        let filledItem = this.model.dynamic_fields[localeWithAllRequiredFields]
        for (let locale in this.model.dynamic_fields) {
          if (locale === localeWithAllRequiredFields) {
            continue;
          }
          for (let fieldKey in filledItem) {
            const tmpVal = filledItem[fieldKey]

            let field = this.model.sorted_dynamic_fields_template.find(f => f.key === fieldKey)
            if (field && field.isAppendable()) {
              field.addChoice(locale, {
                id: tmpVal,
                isNew:true,
                key: tmpVal,
                value: tmpVal
              });
            }

            this.model[`${fieldKey}_${locale}`] = tmpVal
          }
        }
      },
      onDraftSubmit() {
        this.onSubmit(true)
      },
      onSubmitWithoutClose() {
        this.onSubmit(false, false)
      },
      async onSubmit(is_draft = false, withClose = true) {
        this.copyDynamicFieldsToOtherLanguagesIfNecessary()

        this.$nextTick(() => {
          this.runSubmitLogic(is_draft, withClose)
        });
      },
      async runSubmitLogic(is_draft, withClose = true) {
        this.loading = true;
        let validateSuccess = await this.$refs.observer.validate()

        if (this.requiresTranslation) {
          let thirdBtnText = this.$i18n.t('Submit Without Translation');
          let translationRequest = await this.$confirm(
            this.$i18n.t('It looks like you’ve updated the description in the primary language but haven’t translated these changes into other languages yet.\n' +
              '\n' +
              'Would you like to translate the updated text before continuing?'),
            null,
            null,
            null,
            this.$i18n.t('Untranslated Changes Detected'),
            this.$i18n.t('Translate Now'),
            this.$i18n.t('Cancel'),
            null,
            null,
            null,
            'warning',
            thirdBtnText
          );
          if (translationRequest === true) {
            this.requestTranslations();
            return;

          } else if (translationRequest === false) {
            this.loading = false;
            return;
          }
        }

        if (!validateSuccess) {
          const invalidInput = this.$refs.mainForm.querySelector('.has-error');
          if (invalidInput) {
            invalidInput.scrollIntoView({behavior: 'smooth'});
          }
          this.loading = false;
          return
        }

        const reservationProposals = await this.getReservationProposals();
        if (reservationProposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservationProposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) {
            this.loading = false;
            return
          }
        }

        if (this.model.uuid && (
          this.model.tax_status_uuid !== this.initialTaxStatus ||
          (parseFloat(this.model.tax_rate) !== parseFloat(this.initialTaxRate)) ||
          this.model.receipt_uuid !== this.initialReceipt
        )) {
          let success = await this.$confirm(this.$i18n.t('Changing terms will trigger automatic recalculation of all associated invoices and settlements. Would you like to proceed?'));
          if (!success) {
            this.loading = false;
            return
          }
        }

        if (this.model.source_inventories && this.model.source_inventories) {
          this.model.source_inventories.forEach(m => m.resetErrors())
        }

        let modelResponse;

        this.model.assignToDynamicFields();
        let requestData = {...this.model.toJSON(), is_draft}

        if (requestData.collected) {
          let tmpSuccess = await InventoryService.warnOnCollectionForDynamicFields(this, [requestData])
          if (!tmpSuccess) {
            this.loading = false
            return
          }
        }

        if (!this.model.uuid) {
          if (this.isGroupedItem) {
            modelResponse = await this.createGroupedInventory(requestData);
          } else {
            modelResponse = await this.createInventory(requestData);
          }
        } else {
          if (this.isGroupedItem) {
            modelResponse = await this.updateGroupedInventory(requestData);
          } else {
            modelResponse = await this.updateInventory(requestData);
          }
        }

        if (modelResponse.success) {

          let continueLogic = true;

          if (this.images.length > 0) {
            // Returns boolean if images were saved in backend
            continueLogic = await this.$refs.imageViewer.save(modelResponse.body.uuid);
          }

          this.loading = false;
          // Does not continue if image save return false, which means something went wrong
          if (continueLogic) {
            this.showConfirmation = false;

            if (this.shouldRefreshAllDepartmentsAfterSave) {
              this.getDepartments()
            }

            if (!this.model.uuid) {
              this.$toast('success', this.$t('Inventory item was successfully created'));
              if (withClose) {
                this.$router.push({
                  name: 'inventory.view',
                  params: {id: modelResponse.body.uuid}
                });
              }
            } else {
              this.$toast('success', this.$t('Inventory item was successfully updated'));
              if (this.modal.source === SOURCE_INVENTORY_VIEW || this.modal.source === SOURCE_INVENTORY_DASHBOARD) {
                // Silent reload keeps the view mounted (modal lives under the loading gate)
                await this.getInventory({uuid: modelResponse.body.uuid, silent: true});
              }
            }

            if (withClose) {
              this.hideModal();
            } else {
              this.model = new InventoryModel();
              this.itemsInitiallyCollectedData = {
                financial_item_uuids: new Set(),
                reservation_uuids: new Set()
              };
              this.getModalObject({uuid: modelResponse.body.uuid, source: this.modal.source})
            }
          }

        } else {

          this.loading = false;

          if (modelResponse.toastError) {
            this.$errorDialog(modelResponse.toastError);
          } else {
            if (modelResponse.body.non_field_errors) {
              this.$errorDialog(modelResponse.body.non_field_errors[0]);
            } else if (modelResponse.body.reservations) {
              for (let i in modelResponse.body.reservations) {
                this.model.reservations[i].setMultipleErrors(modelResponse.body.reservations[i]);

                const dateErrors = modelResponse.body.reservations[i].dates
                if (dateErrors) {
                  for (let index in dateErrors) {
                    this.model.reservations[i].dates[index].setMultipleErrors(dateErrors[index]);
                  }
                }
              }
            } else if (modelResponse.body.source_inventories) {
              if (modelResponse.body.source_inventories.non_field_errors) {
                this.$errorDialog(modelResponse.body.source_inventories.non_field_errors[0])
              } else {
                for (let i in modelResponse.body.source_inventories) {
                  this.model.source_inventories[i].setMultipleErrors(modelResponse.body.source_inventories[i]);
                }
              }
            } else {
              this.model.setMultipleErrors(modelResponse.body);
              // item_location field is hidden when collected — surface that error in a dialog
              if (modelResponse.body.item_location) {
                const locationErrors = modelResponse.body.item_location;
                this.$errorDialog(Array.isArray(locationErrors) ? locationErrors[0] : locationErrors);
              }
            }
          }
        }
      },
      async addNewReservation(data = {}) {
        let reservation = new ReservationModel(data);
        this.configureContinuousAuctionLotFormDefaultDatesConfiguration(reservation.dates[0]);
        this.model.reservations.push(reservation);
      },
      async addInitialReservation() {
        if (this.model.uuid || this.model.reservations.length > 0 || !this.autoPopulateAuctionInReservation) {
          return
        }

        const currentDateTime = (new Date()).toJSON();
        let response = await SalesService.getSales({
          ordering: 'start_date,created_at',
          start_date: currentDateTime, // Lookup expression for start_date is gte, so this will filter auctions with start date greater than or equal to current date
          expand: 'next_lot_no', page: 1, total: 1
        });

        if (!response.success || !response.body.results || response.body.results.length === 0) {
          return
        }

        let auction = response.body.results[0];

        await this.addNewReservation({auction: auction, auction_uuid: auction.uuid, lot_no: auction.next_lot_no})
      },
      removeReservationByIndex(index) {
        for (let i = 0; i < this.model.reservations.length; i++) {
          if (i === index) {
            this.model.reservations.splice(i, 1);
            break;
          }
        }
        this.getNextLotNumbersForReservations()
      },
      getAmountFieldQuestionText(attribute) {
        if (!this.enableInventoryQuantity) {
          return null;
        }
        let fieldName = this.$options.filters.removeUnderscores(attribute);
        fieldName = this.$options.filters.toUpperCaseFirst(fieldName);
        return this.$i18n.t("{fieldName} amount given for single quantity of the item", {fieldName})
      },
      setCollectionInformation(collection_enabled) {
        if (!collection_enabled) {
          this.model.collection_info_address_1 = null;
          this.model.collection_info_address_2 = null;
          this.model.collection_info_state = null;
          this.model.collection_info_municipality = null;
          this.model.collection_info_city = null;
          this.model.collection_info_zip_code = null;
          this.model.collection_info_phone = null;
          this.model.collection_info_first_name = null;
          this.model.collection_info_last_name = null;
          this.model.collection_info_email = null;
          this.model.collection_info_notes = null;
          this.model.longitude = null;
          this.model.latitude = null;
        } else {
          if (this.model.receipt && this.model.receipt.customer && this.model.receipt.customer.shipping_address) {
            this.model.collection_info_address_1 = this.model.collection_info_address_1 ? this.model.collection_info_address_1 : this.model.receipt.customer.shipping_address.address_1;
            this.model.collection_info_address_2 = this.model.collection_info_address_2 ? this.model.collection_info_address_2 : this.model.receipt.customer.shipping_address.address_2;
            this.model.collection_info_state = this.model.collection_info_state ? this.model.collection_info_state : this.model.receipt.customer.shipping_address.state;
            this.model.collection_info_city = this.model.collection_info_city ? this.model.collection_info_city : this.model.receipt.customer.shipping_address.city;
            this.model.collection_info_zip_code = this.model.collection_info_zip_code ? this.model.collection_info_zip_code : this.model.receipt.customer.shipping_address.zip_code;
            this.model.collection_info_first_name = this.model.collection_info_first_name ? this.model.collection_info_first_name : this.model.receipt.customer.shipping_address.first_name;
            this.model.collection_info_last_name = this.model.collection_info_last_name ? this.model.collection_info_last_name : this.model.receipt.customer.shipping_address.last_name;
            this.model.collection_info_phone = this.model.collection_info_phone ? this.model.collection_info_phone : this.model.receipt.customer.shipping_address.phone ? this.model.receipt.customer.shipping_address.phone.split(',')[0] : this.model.receipt.customer.phone ? this.editObject.receipt.customer.phone.split(',')[0] : null;
            this.model.collection_info_email = this.model.collection_info_email ? this.model.collection_info_email : this.model.receipt.customer.shipping_address.email ? this.model.receipt.customer.shipping_address.email : this.model.receipt.customer.email ? this.editObject.receipt.customer.email : null;
          } else if (this.isGroupedItem && this.model.customer.shipping_address) {
            this.model.collection_info_address_1 = this.model.customer.shipping_address.address_1;
            this.model.collection_info_address_2 = this.model.customer.shipping_address.address_2;
            this.model.collection_info_state = this.model.customer.shipping_address.state;
            this.model.collection_info_city = this.model.customer.shipping_address.city;
            this.model.collection_info_zip_code = this.model.customer.shipping_address.zip_code;
            this.model.collection_info_first_name = this.model.customer.shipping_address.first_name;
            this.model.collection_info_last_name = this.model.customer.shipping_address.last_name;
            this.model.collection_info_phone = this.model.customer.shipping_address.phone ? this.model.customer.shipping_address.phone.split(',')[0] : this.model.customer.phone ? this.editObject.customer.phone.split(',')[0] : null;
            this.model.collection_info_email = this.model.customer.shipping_address.email ? this.model.customer.shipping_address.email : this.model.customer.email ? this.editObject.customer.email : null;
          }
        }
      },
      init() {
        this.model.financial_items = this.model.financial_items.map(data => new FinancialItemModel({
          ...data,
          invoice_no: data.invoice.invoice_no
        }));
      },
      async onCheckCodeClick() {
        if (!this.model.category_uuid) {
          this.model.setErrors('category_uuid', [this.$t('This field is required')])
          return
        }
        this.checking = true;
        const data = {
          category_uuid: this.model.category_uuid,
          black_book: this.model.black_book_en,
          spec: this.model.spec_en,
          mot: this.model.mot_en,
          vin_number: this.model.vin_number_en,
          vrm_number: this.model.vrm_number_en,
          mileage: this.model.mileage_en,
        }
        let response;
        response = await InventoryService.checkVimVrmCode(data);
        if (response.success) {
          this.model = new InventoryModel({...this.model, dynamic_fields: response.body.dynamic_fields})
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            } else {
              this.model.setMultipleErrors(response.body);
            }
          }
        }
        this.checking = false;
      },
      calcAvailableQuantity() {
        let unavailable_quantity = 0
        if (this.model && this.model.reservations) {
          this.model.reservations.forEach(item => {
            if ((item.status !== 'withdrawn' && item.status !== 'unsold') || item.collected) {
              if (parseInt(item.quantity)) {
                unavailable_quantity += parseInt(item.quantity);
              }
            }
          });
        }
        if (this.model && this.model.financial_items) {
          this.model.financial_items.forEach(item => {
            if (parseInt(item.quantity)) {
              unavailable_quantity += parseInt(item.quantity);
            }
          });
        }
        if (this.model && this.model.grouped_inventories) {
          this.model.grouped_inventories.forEach(groupedItem => {
            unavailable_quantity += parseInt(groupedItem.quantity);
          })
        }
        let available_quantity = this.model.quantity - unavailable_quantity;
        this.model.available_quantity = available_quantity < 0 ? 0 : available_quantity;
        unavailable_quantity = 0;
      },
      async onShowGoogleMapsClick() {
        let params = {}
        if (this.model.longitude !== null || this.model.latitude !== null) {
          params = {
            lng: Number(this.model.longitude),
            lat: Number(this.model.latitude)
          };
        } else {
          const response = await this.getLongAndLat();
          params = {...response};
        }
        this.showGoogleMapsModal(params);
      },
      onCoordinateSelect(coordinates) {
        this.model.longitude = coordinates.lng;
        this.model.latitude = coordinates.lat;
      },
      isGlobeDisabled(type) {
        const observer = this.$refs.observer
        return !!(observer && Object.keys(observer.errors).length && observer.errors[`item-${type}`] && observer.errors[`item-${type}`].length);
      },
      onReceiptSelect(data) {
        this.model.receipt = data
        if (this.model.uuid) {
          this.model.customer_uuid = data.customer_uuid;
        } else {
          let itemLocationUuid = this.defaultItemLocation ? this.defaultItemLocation.uuid : null
          itemLocationUuid = (this.model.receipt && this.model.receipt.item_locations && this.model.receipt.item_locations.length) ? this.model.receipt.item_locations[0].item_location_uuid : itemLocationUuid
          this.model.item_location_uuid = itemLocationUuid
        }
        this.model.inheritDynamicFieldsValues();
      },
      onAddNewSourceInventoryClick() {
        this.model.source_inventories.push(new SourceInventoryModel());
      },
      removeSourceInventoryByIndex(index) {
        for (let i = 0; i < this.model.source_inventories.length; i++) {
          if (i === index) {
            this.model.source_inventories.splice(i, 1);
            break;
          }
        }
      },
      onQuantityChange: debounce(function () {
        this.calculateReservePerUnit();
      }, 300),
      onReserveChange: debounce(function () {
        this.calculateReservePerUnit();
      }, 300),
      calculateReservePerUnit() {
        if (!this.showPricePerUnitField) {
          return false;
        }

        if (this.model.reserve && this.model.quantity) {
          this.model.reserve_per_unit = parseFloat(parseFloat(this.model.reserve) / parseFloat(this.model.quantity)).toFixed(2)
        }
      },
      async getReservationProposals() {
        const reservationProposals = [];
        const proposalsPayload = {}
        const {financial_item_uuids, reservation_uuids} = this.getCollectedItemsData()

        if (this.model.collected && !this.itemInitiallyCollected) {
          proposalsPayload['inventory_uuids'] = [this.model.uuid]
        } else {
          const newlyCollectedReservations = reservation_uuids.difference(this.itemsInitiallyCollectedData.reservation_uuids)
          if (newlyCollectedReservations) {
            proposalsPayload['reservation_uuids'] = [...newlyCollectedReservations]
          }
          const newlyCollectedFinancialItems = financial_item_uuids.difference(this.itemsInitiallyCollectedData.financial_item_uuids)
          if (newlyCollectedFinancialItems) {
            proposalsPayload['financial_item_uuids'] = [...newlyCollectedFinancialItems]
          }
        }

        if ((proposalsPayload.reservation_uuids && proposalsPayload.reservation_uuids.length)
          || (proposalsPayload.financial_item_uuids && proposalsPayload.financial_item_uuids.length)
          || (proposalsPayload.inventory_uuids && proposalsPayload.inventory_uuids.length)) {
          const reservationProposalsResponse = (await LotsService.getLatestReservationProposals({
            ...proposalsPayload,
            suggestions: ['reoffer'],
          })).body
          reservationProposals.push(...reservationProposalsResponse.filter(proposal => !reservationProposals.some(existing => existing.uuid === proposal.uuid)))
        }

        if (this.model &&
          this.editObject &&
          this.model.reservations &&
          Array.isArray(this.editObject.reservations) &&
          this.model.reservations.length > 0 &&
          this.editObject.reservations.length < this.model.reservations.length) {
          const reservationProposalsResponse = (await LotsService.getLatestReservationProposals({
            stock_nos: [this.model.stock_no],
            suggestions: ['return', 'transfer'],
          })).body
          reservationProposals.push(...reservationProposalsResponse.filter(proposal => !reservationProposals.some(existing => existing.uuid === proposal.uuid)))
        }

        return reservationProposals;
      },
      onDynamicFieldTranslationInputChange(value, locale, field) {
        if (!this.translationProvider) {
          return;
        }
        if (this.translationProvider.from_language === locale && field.translatable
          && ['textarea', 'text', 'ckeditor'].indexOf(field.type) > -1) {
          this.requiresTranslation = true;
        }
      },
      async requestTranslations() {
        this.loading = true;
        const data = [];
        this.computedDynamicFields.forEach(field => {
          if (field.translatable && ['textarea', 'text', 'ckeditor'].indexOf(field.type) > -1 && this.model[`${field.key}_${this.translationProvider.from_language}`]) {
            data.push({
              key: field.key,
              html: field.type === 'ckeditor',
              value: this.model[`${field.key}_${this.translationProvider.from_language}`]
            });
          }
        });
        const identifier = uuidV4();
        if (!data.length) {
          this.loading = false;
          this.$toast('success', this.$i18n.t('No translations to request'))
          return;
        }

        this.$toast('warning', this.$i18n.t("Pulling translations, please do not close this window or reload the page.."))
        await InventoryService.requestTranslations({'identifier': identifier, 'data': data});

        this.$ws.listen(`inventories.${WS_TRANSLATION_REQUEST}`, (res) => {
          if (res.identifier !== identifier) {
            return;
          }
          if (res.finish) {
            this.loading = false;
            this.requiresTranslation = false;
            this.$ws.remove(`inventories.${WS_TRANSLATION_REQUEST}`)
            this.$toast('success', this.$i18n.t("Translations pulled successfully."))
          } else {
            let language = res.language.toLowerCase().split(/[-_]/)[0];
            this.model[`${res.key}_${language}`] = res.value;
          }

        });
      }
    },
    beforeMount() {
      if (this.isGoogleMapsEnabled && !this.isGoogleApiSetUp) {
        this.changeGoogleApiSetUp(true);
        loadGmapApi({
          key: this.googleMapToken,
          libraries: 'places'
        })
      }
    },
  }
</script>

<style lang="scss" scoped>

  .check-btn {
    margin-top: 28px;
  }

  .collected-checkbox {
    max-width: 90px;
    min-width: 55px;
  }

  .globe-container {
    max-height: 78.39px;

    > button {
      margin-top: 12px;
    }
  }

  .collection-info-card {
    .card-header {
      /deep/ .form-group {
        margin-bottom: 0;
      }
    }
  }
</style>
